import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ThemeContext } from 'styled-components';
import { validateSpectrum } from '../../types/fft-spectrum';
import { findSpectrumPeaks } from '../../utils/fft-peaks';
import * as s from './styled-fft-spectrum';

// Vibration FFT spectrum chart. Bars per bin (never an interpolated line),
// drawn on a <canvas> so it stays smooth from 64 up to 2048 bins. Presentation
// only: it takes a normalized, already-sourced spectrum and the peak detector
// output; it does not fetch or generate data.

const PLOT_MARGIN = { top: 20, right: 16, bottom: 30, left: 54 };
const MIN_X_LABEL_PX = 46; // used for tick autoSkip
const PEAK_COUNT = 5;
const MEDIAN_FACTOR = 3;
// Thin "stem" look (like a real FFT spectrum): a fixed narrow bar centered on
// each bin's frequency, independent of bin count. At high resolutions the slot
// is already sub-pixel, so this only matters for low bin counts (e.g. 64),
// where fat bars read as a solid block. Peaks get a touch more width to stand
// out without becoming fat.
const BAR_MAX_PX = 2;
const PEAK_MIN_PX = 3;

const formatHz = (hz) => {
  const rounded = hz >= 100 ? Math.round(hz) : Math.round(hz * 10) / 10;
  return rounded.toLocaleString('pt-BR');
};

const formatAmp = (v) => {
  const abs = Math.abs(v);
  const digits = abs >= 100 ? 1 : abs >= 1 ? 2 : 3;
  return v.toLocaleString('pt-BR', { maximumFractionDigits: digits });
};

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString('pt-BR');
};

// "Nice" linear ticks from 0 to max (~targetCount steps, rounded to 1/2/5).
const linearTicks = (max, targetCount = 5) => {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const rough = max / targetCount;
  const exp = Math.floor(Math.log10(rough));
  const frac = rough / 10 ** exp;
  let niceFrac;
  if (frac < 1.5) niceFrac = 1;
  else if (frac < 3) niceFrac = 2;
  else if (frac < 7) niceFrac = 5;
  else niceFrac = 10;
  const step = niceFrac * 10 ** exp;
  const ticks = [];
  for (let v = 0; v <= max + step * 0.5; v += step) {
    ticks.push(Number(v.toFixed(10)));
  }
  return ticks;
};

// Decade ticks for a log axis between floor and max (e.g. 0.01, 0.1, 1, 10).
const logTicks = (floor, max) => {
  const ticks = [];
  const from = Math.floor(Math.log10(floor));
  const to = Math.ceil(Math.log10(max));
  for (let e = from; e <= to; e += 1) ticks.push(10 ** e);
  return ticks;
};

const FftSpectrum = ({
  spectrum,
  scaleType = 'linear',
  onScaleChange,
  locked = false,
  lockedMax = null,
  onLockChange,
}) => {
  const { colors } = useContext(ThemeContext);
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState(null);

  const validation = useMemo(() => validateSpectrum(spectrum), [spectrum]);
  const valid = validation.ok;

  // Peaks: top N local maxima above MEDIAN_FACTOR x median, annotated with
  // order/BPF from the spectrum's own rpm/bladeCount. Pure util, memoized.
  const peaks = useMemo(() => {
    if (!valid) return [];
    return findSpectrumPeaks(spectrum, {
      count: PEAK_COUNT,
      medianFactor: MEDIAN_FACTOR,
    });
  }, [spectrum, valid]);

  const peakByBin = useMemo(() => {
    const map = new Map();
    peaks.forEach((p) => map.set(p.binIndex, p));
    return map;
  }, [peaks]);

  // Track container width (height is fixed by CSS) for a crisp, responsive
  // canvas. Throttle-free: ResizeObserver already coalesces.
  useEffect(() => {
    const node = wrapRef.current;
    if (!node) return undefined;
    const measure = () =>
      setSize({ width: node.clientWidth || 0, height: node.clientHeight || 0 });
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Geometry shared by the drawing effect and the hover handler, so the
  // tooltip maps pixels back to the exact bin that was drawn.
  const geom = useMemo(() => {
    if (!valid || !size.width || !size.height) return null;
    const { binCount, binStep, magnitudes } = spectrum;
    const nyquist = binCount * binStep;
    const plotW = size.width - PLOT_MARGIN.left - PLOT_MARGIN.right;
    const plotH = size.height - PLOT_MARGIN.top - PLOT_MARGIN.bottom;

    // Y domain from 0 (linear) or a positive floor (log). Bin 0 (DC) excluded.
    let dataMax = 0;
    let dataMinPos = Infinity;
    for (let i = 1; i < binCount; i += 1) {
      const v = magnitudes[i];
      if (v > dataMax) dataMax = v;
      if (v > 0 && v < dataMinPos) dataMinPos = v;
    }
    const yMax =
      locked && Number.isFinite(lockedMax) && lockedMax > 0
        ? lockedMax
        : dataMax || 1;
    const logFloor = Math.max(
      Number.isFinite(dataMinPos) ? dataMinPos : yMax / 1000,
      yMax / 1000
    );

    const xToPx = (hz) => PLOT_MARGIN.left + (hz / nyquist) * plotW;
    const baseY = PLOT_MARGIN.top + plotH;
    const yToPx = (val) => {
      if (scaleType === 'log') {
        const v = Math.max(val, logFloor);
        const ratio =
          (Math.log10(v) - Math.log10(logFloor)) /
          (Math.log10(yMax) - Math.log10(logFloor) || 1);
        return PLOT_MARGIN.top + plotH * (1 - Math.max(0, Math.min(1, ratio)));
      }
      return PLOT_MARGIN.top + plotH * (1 - Math.max(0, Math.min(1, val / yMax)));
    };

    return {
      binCount,
      binStep,
      nyquist,
      plotW,
      plotH,
      xToPx,
      yToPx,
      baseY,
      yMax,
      logFloor,
    };
  }, [valid, size, spectrum, scaleType, locked, lockedMax]);

  // Draw the spectrum whenever geometry, data, scale or theme changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !geom || !valid) return;
    const { magnitudes, binCount, binStep, unit } = spectrum;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.width * dpr;
    canvas.height = size.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);

    const floorColor = colors.blue;
    const peakColor = colors.orange;
    const gridColor = colors.disabled;
    const labelColor = colors.label;

    ctx.font =
      '10px -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
    ctx.textBaseline = 'middle';

    // --- Y grid + ticks ---
    const yTicks =
      scaleType === 'log'
        ? logTicks(geom.logFloor, geom.yMax)
        : linearTicks(geom.yMax, 5);
    ctx.strokeStyle = gridColor;
    ctx.fillStyle = labelColor;
    ctx.lineWidth = 1;
    ctx.textAlign = 'right';
    yTicks.forEach((t) => {
      if (t > geom.yMax * 1.0001) return;
      const y = geom.yToPx(t);
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.setLineDash([3, 3]);
      ctx.moveTo(PLOT_MARGIN.left, y);
      ctx.lineTo(size.width - PLOT_MARGIN.right, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      ctx.fillText(formatAmp(t), PLOT_MARGIN.left - 8, y);
    });

    // Y axis unit label (top-left).
    ctx.textAlign = 'left';
    ctx.fillStyle = labelColor;
    ctx.fillText(unit, PLOT_MARGIN.left - 46, PLOT_MARGIN.top - 10);

    // --- X ticks (every 100 Hz, or 500 if range > 2 kHz) with autoSkip ---
    let step = geom.nyquist > 2000 ? 500 : 100;
    const maxLabels = Math.max(2, Math.floor(geom.plotW / MIN_X_LABEL_PX));
    while (geom.nyquist / step > maxLabels) step *= 2;
    ctx.textAlign = 'center';
    ctx.fillStyle = labelColor;
    for (let hz = 0; hz <= geom.nyquist + 0.5; hz += step) {
      const x = geom.xToPx(hz);
      ctx.fillText(formatHz(hz), x, geom.baseY + 14);
    }
    // Axis caption "Hz" at the right end of the X axis.
    ctx.textAlign = 'right';
    ctx.fillText('Hz', size.width - PLOT_MARGIN.right, geom.baseY + 14);

    // Baseline (X axis line).
    ctx.strokeStyle = gridColor;
    ctx.globalAlpha = 0.6;
    ctx.beginPath();
    ctx.moveTo(PLOT_MARGIN.left, geom.baseY);
    ctx.lineTo(size.width - PLOT_MARGIN.right, geom.baseY);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // --- Bars (bin 0 / DC skipped). Thin stems capped at BAR_MAX_PX so a
    //     64-bin spectrum reads as needles, not a solid block; peak bars are
    //     drawn last, a touch wider and highlighted, so they stay legible. ---
    const slot = geom.plotW / binCount;
    const barW = Math.max(1, Math.min(slot - 1, BAR_MAX_PX));
    ctx.fillStyle = floorColor;
    for (let i = 1; i < binCount; i += 1) {
      // Peak bins are drawn in the highlight pass below.
      if (!peakByBin.has(i)) {
        const x = geom.xToPx(i * binStep);
        const y = geom.yToPx(magnitudes[i]);
        const h = geom.baseY - y;
        if (h > 0) ctx.fillRect(x - barW / 2, y, barW, h);
      }
    }
    // Peak bars on top.
    ctx.fillStyle = peakColor;
    peaks.forEach((p) => {
      const x = geom.xToPx(p.freq);
      const y = geom.yToPx(p.amplitude);
      const h = geom.baseY - y;
      const w = Math.max(PEAK_MIN_PX, barW);
      if (h > 0) ctx.fillRect(x - w / 2, y, w, h);
    });

    // --- Peak labels (freq + order), in secondary text color, never series. ---
    ctx.fillStyle = labelColor;
    ctx.textAlign = 'center';
    peaks.forEach((p) => {
      const x = Math.max(
        PLOT_MARGIN.left + 14,
        Math.min(size.width - PLOT_MARGIN.right - 14, geom.xToPx(p.freq))
      );
      const topY = Math.max(PLOT_MARGIN.top + 6, geom.yToPx(p.amplitude) - 8);
      if (p.order) {
        ctx.font =
          '700 10px -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
        ctx.fillText(p.order, x, topY - 10);
      }
      ctx.font =
        '10px -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
      ctx.fillText(`${formatHz(p.freq)} Hz`, x, topY);
    });
  }, [geom, valid, spectrum, size, scaleType, colors, peaks, peakByBin]);

  const handleMove = (event) => {
    if (!geom) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = event.clientX - rect.left;
    const my = event.clientY - rect.top;
    if (
      mx < PLOT_MARGIN.left ||
      mx > size.width - PLOT_MARGIN.right ||
      my < PLOT_MARGIN.top ||
      my > geom.baseY
    ) {
      setTooltip(null);
      return;
    }
    const hz = ((mx - PLOT_MARGIN.left) / geom.plotW) * geom.nyquist;
    const bin = Math.max(1, Math.min(geom.binCount - 1, Math.round(hz / geom.binStep)));
    const amp = spectrum.magnitudes[bin];
    setTooltip({
      x: geom.xToPx(bin * geom.binStep),
      y: geom.yToPx(amp),
      freq: bin * geom.binStep,
      amp,
    });
  };

  // --- Empty / error states (never a blank chart) ---
  if (!spectrum) {
    return (
      <s.Container>
        <s.StateBox>Sem leitura FFT disponível.</s.StateBox>
      </s.Container>
    );
  }
  if (!valid) {
    return (
      <s.Container>
        <Header
          spectrum={spectrum}
          scaleType={scaleType}
          onScaleChange={onScaleChange}
          locked={locked}
          onLockChange={onLockChange}
        />
        <s.StateBox $error>Dado FFT inválido: {validation.error}</s.StateBox>
      </s.Container>
    );
  }

  return (
    <s.Container>
      <Header
        spectrum={spectrum}
        scaleType={scaleType}
        onScaleChange={onScaleChange}
        locked={locked}
        onLockChange={onLockChange}
      />
      <s.CanvasWrap ref={wrapRef}>
        <canvas
          ref={canvasRef}
          onMouseMove={handleMove}
          onMouseLeave={() => setTooltip(null)}
        />
        {tooltip && (
          <s.Tooltip style={{ left: tooltip.x, top: tooltip.y }}>
            <span>{formatHz(tooltip.freq)} Hz</span>
            <strong>
              {formatAmp(tooltip.amp)} {spectrum.unit}
            </strong>
          </s.Tooltip>
        )}
      </s.CanvasWrap>
      <s.Legend>
        <s.LegendItem>
          <s.Swatch color={colors.blue} /> Espectro
        </s.LegendItem>
        <s.LegendItem>
          <s.Swatch color={colors.orange} /> Picos (N={PEAK_COUNT})
        </s.LegendItem>
      </s.Legend>
    </s.Container>
  );
};

// Header: axis name, "N bins · passo X Hz", timestamp — all from the data —
// plus the linear/log and locked-scale controls.
const Header = ({ spectrum, scaleType, onScaleChange, locked, onLockChange }) => {
  const axisLabel = String(spectrum.axis || '').toUpperCase();
  return (
    <s.Header>
      <s.HeaderMain>
        <s.Title>Espectro FFT{axisLabel}</s.Title>
        <s.Meta>
          {spectrum.binCount} bins · passo{' '}
          {spectrum.binStep.toLocaleString('pt-BR', {
            maximumFractionDigits: 2,
          })}{' '}
          Hz
        </s.Meta>
      </s.HeaderMain>
      <s.HeaderRight>
        <s.Timestamp>{formatTimestamp(spectrum.timestamp)}</s.Timestamp>
        <s.Controls>
          <s.Segmented role="group" aria-label="Escala vertical">
            <s.SegButton
              type="button"
              $active={scaleType === 'linear'}
              onClick={() => onScaleChange && onScaleChange('linear')}
            >
              Linear
            </s.SegButton>
            <s.SegButton
              type="button"
              $active={scaleType === 'log'}
              onClick={() => onScaleChange && onScaleChange('log')}
            >
              Log
            </s.SegButton>
          </s.Segmented>
          <s.ToggleButton
            type="button"
            $active={locked}
            aria-pressed={locked}
            title="Trava a escala Y igual entre os três eixos para comparar"
            onClick={() => onLockChange && onLockChange(!locked)}
          >
            Escala travada
          </s.ToggleButton>
        </s.Controls>
      </s.HeaderRight>
    </s.Header>
  );
};

export default FftSpectrum;
